'use client'

// Vertical DNA double helix decoration. Two sine-wave strands plus
// "rungs" between them, with subtle base-pair colour variation.
// Drawn at viewBox 80×320, intended to sit in section margins as a
// thin column. CSS animation slowly cycles strand colour/opacity to
// suggest "alive" without being distracting.

const OLIVE = '#5B7005'
const LIME  = '#9FAF08'
const EARTH = 'rgba(81,50,5,0.5)'

const RUNG_COUNT = 14
const TOTAL_HEIGHT = 320
const STRAND_AMP = 18  // x-amplitude of the sine wave
const CENTER_X = 40

interface Props {
  height?: number       // px; defaults to inheriting parent height
  className?: string
}

export default function DNAHelix({ height = 320, className }: Props) {
  // Build the two strand SVG paths as cubic-bezier sine approximations
  const segHeight = TOTAL_HEIGHT / RUNG_COUNT
  const path1Points: string[] = []
  const path2Points: string[] = []

  for (let i = 0; i <= RUNG_COUNT; i++) {
    const y = i * segHeight
    const phase1 = Math.sin((i / RUNG_COUNT) * Math.PI * 4)         // 4 full cycles
    const phase2 = Math.sin((i / RUNG_COUNT) * Math.PI * 4 + Math.PI) // offset by π
    const x1 = CENTER_X + STRAND_AMP * phase1
    const x2 = CENTER_X + STRAND_AMP * phase2
    path1Points.push(`${i === 0 ? 'M' : 'L'} ${x1.toFixed(2)} ${y.toFixed(2)}`)
    path2Points.push(`${i === 0 ? 'M' : 'L'} ${x2.toFixed(2)} ${y.toFixed(2)}`)
  }

  // Smoother strand using quadratic curve segments
  const smoothPath = (pts: string[]) => {
    const tokens = pts.map(p => p.split(' ').slice(1).map(Number) as [number, number])
    let d = `M ${tokens[0][0]} ${tokens[0][1]}`
    for (let i = 1; i < tokens.length; i++) {
      const [x, y]   = tokens[i]
      const [px, py] = tokens[i - 1]
      const cy = (py + y) / 2
      d += ` C ${px} ${cy}, ${x} ${cy}, ${x} ${y}`
    }
    return d
  }

  return (
    <div className={className} style={{ height, width: 80, position: 'relative' }}>
      <style>{`
        @keyframes helixPulse {
          0%, 100% { opacity: 0.7; }
          50%      { opacity: 1.0; }
        }
        .dna-rung {
          animation: helixPulse 3.6s ease-in-out infinite;
        }
        @media (prefers-reduced-motion: reduce) {
          .dna-rung { animation: none; opacity: 0.85; }
        }
      `}</style>
      <svg
        viewBox={`0 0 80 ${TOTAL_HEIGHT}`}
        preserveAspectRatio="none"
        style={{ width: '100%', height: '100%', display: 'block', overflow: 'visible' }}
        aria-hidden="true"
      >
        {/* Rungs — base pairs connecting the strands */}
        {Array.from({ length: RUNG_COUNT }).map((_, i) => {
          const y = (i + 0.5) * segHeight
          const phase1 = Math.sin(((i + 0.5) / RUNG_COUNT) * Math.PI * 4)
          const phase2 = Math.sin(((i + 0.5) / RUNG_COUNT) * Math.PI * 4 + Math.PI)
          const x1 = CENTER_X + STRAND_AMP * phase1
          const x2 = CENTER_X + STRAND_AMP * phase2
          // Alternate rung colour to suggest A-T vs C-G base pairs
          const isPurine = i % 2 === 0
          return (
            <line
              key={i}
              className="dna-rung"
              x1={x1} y1={y}
              x2={x2} y2={y}
              stroke={isPurine ? OLIVE : LIME}
              strokeWidth={1.2}
              opacity={0.55}
              style={{ animationDelay: `${i * 0.18}s` }}
            />
          )
        })}

        {/* Strand 1 (cream-edge olive) */}
        <path
          d={smoothPath(path1Points)}
          fill="none"
          stroke={OLIVE}
          strokeWidth={1.4}
          strokeLinecap="round"
        />
        {/* Strand 2 (lime accent) */}
        <path
          d={smoothPath(path2Points)}
          fill="none"
          stroke={LIME}
          strokeWidth={1.4}
          strokeLinecap="round"
        />

        {/* Sample nucleotide letters at intervals */}
        {[2, 6, 10, 13].map(i => {
          const y = (i + 0.5) * segHeight
          const phase = Math.sin(((i + 0.5) / RUNG_COUNT) * Math.PI * 4)
          const x = CENTER_X + (STRAND_AMP + 12) * (phase >= 0 ? 1 : -1)
          const letter = ['A', 'T', 'C', 'G'][i % 4]
          return (
            <text
              key={`n-${i}`}
              x={x}
              y={y + 3}
              textAnchor="middle"
              style={{
                fontFamily: 'var(--font-body)',
                fontSize: 7,
                fontWeight: 700,
                letterSpacing: '0.05em',
                fill: EARTH,
              }}
            >
              {letter}
            </text>
          )
        })}
      </svg>
    </div>
  )
}
